import React, {useEffect, useRef} from 'react';
import * as echarts from 'echarts/core';
import {LineChart, BarChart, PieChart} from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  DatasetComponent,
  PolarComponent,
} from 'echarts/components';
import {CanvasRenderer} from 'echarts/renderers';

// Register only what the dashboard uses (keeps the bundle tree-shaken).
echarts.use([
  LineChart,
  BarChart,
  PieChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  DatasetComponent,
  PolarComponent,
  CanvasRenderer,
]);

/**
 * Thin React wrapper around ECharts.
 * - Fills its parent (parent must set a height).
 * - Resizes with the container via ResizeObserver.
 * - Re-applies the option whenever it changes (replace, notMerge).
 * - Disposes on unmount.
 */
const EChart = ({option, className, style, notMerge = true}) => {
  const elRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return undefined;
    const chart = echarts.init(el, null, {renderer: 'canvas'});
    chartRef.current = chart;

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => chart.resize());
      ro.observe(el);
    } else {
      window.addEventListener('resize', chart.resize);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', chart.resize);
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (chartRef.current && option) {
      chartRef.current.setOption(option, notMerge);
    }
  }, [option, notMerge]);

  return (
    <div
      ref={elRef}
      className={className}
      style={{width: '100%', height: '100%', ...style}}
    />
  );
};

export default EChart;
